import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useNotificationsLastSeen } from "@mirage/service-dash-trial/hooks/useNotificationsLastSeen";
import { useAtom } from "jotai";
import { useCallback } from "react";
import { trialEndingNotificationAtom } from "./atoms";
import { TrialActionType } from "./types";
import { getActionSurface, useLogShownUpsellComponent } from "./utils";

export const TRIGGER_CHAT_UPSELL_AFTER_DAYS_LEFT = 15;

export const useChatTrialUpsell = () => {
  const [trialEndingNotification, setTrialEndingNotification] = useAtom(
    trialEndingNotificationAtom,
  );
  const { setLastSeen, lastSeenChatNotification } = useNotificationsLastSeen();
  const logShownUpsell = useLogShownUpsellComponent();
  const isAdmin = useIsAdminUser();
  const { daysLeft } = trialEndingNotification;

  const setChatContainerRef = useCallback(
    (ref: React.RefObject<HTMLElement> | null) => {
      setTrialEndingNotification((prev) => ({
        ...prev,
        chatContainerRef: ref,
      }));
    },
    [setTrialEndingNotification],
  );

  const showChatTrialNotification = useCallback(
    (daysLeft: number, containerRef?: React.RefObject<HTMLElement>) => {
      setTrialEndingNotification((prev) => ({
        ...prev,
        actionType: TrialActionType.Chat,
        isVisible: true,
        containerRef: containerRef || prev.chatContainerRef,
      }));
      setLastSeen({ chat: daysLeft });
      logShownUpsell({
        actionSurface: getActionSurface(TrialActionType.Chat),
        ctaName: isAdmin ? "view_plans" : "ask_your_admin",
      });
    },
    [setTrialEndingNotification, setLastSeen, logShownUpsell],
  );

  const chatCopiedTriggerUpsellCheck = useCallback(() => {
    if (
      daysLeft < lastSeenChatNotification &&
      daysLeft <= TRIGGER_CHAT_UPSELL_AFTER_DAYS_LEFT
    ) {
      showChatTrialNotification(daysLeft);
    }
  }, [lastSeenChatNotification, showChatTrialNotification, daysLeft]);

  const isChatTrialNotificationVisible = trialEndingNotification.isVisible;

  const hideTrialNotification = useCallback(() => {
    setTrialEndingNotification((prev) => ({
      ...prev,
      isVisible: false,
    }));
  }, [setTrialEndingNotification]);

  return {
    chatCopiedTriggerUpsellCheck,
    hideTrialNotification,
    isChatTrialNotificationVisible,
    setChatContainerRef,
    showChatTrialNotification,
  };
};
